import React from 'react';
import { Box, Typography } from '@mui/material';
import type { ImageBlock } from '../../api/types';

interface ImageBlockViewProps {
  block: ImageBlock;
  /**
   * fill    = consume the available box (slide pages, canvas elements):
   *           parent must provide a bounded height (flex track or fixed box).
   * bounded = compact contexts (columns): real box so objectFit works,
   *           but capped height instead of page-fill.
   */
  sizing: 'fill' | 'bounded';
}

/**
 * Renders an ImageBlock on a slide. The <img> always gets a real box so the
 * block's `fit` (contain|cover) is meaningful — REP-13's root cause was a
 * bare max-height cap that left objectFit nothing to act on.
 */
export const ImageBlockView: React.FC<ImageBlockViewProps> = ({ block, sizing }) => {
  const fit = block.fit ?? 'contain';

  if (sizing === 'bounded') {
    return (
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Box
          component='img'
          src={block.url}
          alt={block.caption ?? ''}
          sx={{
            width: '100%',
            maxHeight: 320,
            objectFit: fit,
            borderRadius: 1,
            display: 'block',
          }}
        />
        {block.caption && (
          <Typography variant='caption' sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            {block.caption}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      <Box
        component='img'
        src={block.url}
        alt={block.caption ?? ''}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          objectFit: fit,
          borderRadius: 1,
        }}
      />
      {block.caption && (
        <Typography variant='caption' sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          {block.caption}
        </Typography>
      )}
    </Box>
  );
};
